import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImportFileUploadStepProps {
  file: File | null;
  busy: boolean;
  onFileSelect: (file: File | null) => void;
}

const MEGABYTE = 1024 * 1024;

export default function ImportFileUploadStep({ file, busy, onFileSelect }: ImportFileUploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  function openFilePicker() {
    if (busy || !fileInputRef.current) {
      return;
    }
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  }

  function clearSelectedFile(event: React.MouseEvent) {
    event.stopPropagation();
    onFileSelect(null);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!busy) {
      setDragging(true);
    }
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (busy) {
      return;
    }
    onFileSelect(event.dataTransfer.files?.[0] ?? null);
  }

  function formatFileSize(bytes: number): string {
    if (bytes < MEGABYTE) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / MEGABYTE).toFixed(1)} MB`;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openFilePicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          openFilePicker();
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "flex cursor-pointer flex-col items-center gap-3 rounded-[var(--radius)] border border-dashed border-border p-8 text-center transition-colors hover:bg-muted/40",
        dragging && "border-primary bg-primary/5",
      )}
    >
      {file ? (
        <div className="flex w-full max-w-md items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-left">
          <FileSpreadsheet className="h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{file.name}</div>
            <div className="text-xs tabular-nums text-muted-foreground">{formatFileSize(file.size)}</div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={clearSelectedFile} disabled={busy} title="Remove file">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <Upload className="h-6 w-6 text-muted-foreground" />
          <div className="space-y-1">
            <div className="text-sm font-medium">Drop a file here or click to choose one</div>
            <div className="text-xs text-muted-foreground">CSV or Excel (.xlsx), up to 100 MB</div>
          </div>
        </>
      )}

      <Input
        ref={fileInputRef}
        className="hidden"
        type="file"
        accept=".csv,.tsv,.txt,.xlsx"
        multiple={false}
        onChange={(event) => onFileSelect(event.target.files?.[0] ?? null)}
      />
    </div>
  );
}
